import React, { useEffect, useState } from "react";
import { Button, Form, Input, Typography, message } from "antd";
import { ArrowLeftOutlined, MailOutlined, NumberOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirebase } from "react-redux-firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dashboard } from "../../../urls";
import { requestLoginCode, verifyLoginCode } from "../../../functions/api/cmsAuth";

const { Text } = Typography;
const RESEND_COOLDOWN_SECONDS = 30;
const CODE_LENGTH = 6;

/**
 * Inloggen met een e-mailcode, in twee stappen:
 * 1. e-mailadres → API stuurt een code (alleen aan adressen met CMS-toegang;
 *    het antwoord is altijd gelijk, dus de pagina zegt nooit "onbekend adres");
 * 2. code → API geeft een Firebase custom token → inloggen.
 * Daarna loopt alles zoals voorheen (RequireCmsRole, permissies).
 *
 * Er is geen wachtwoord meer in het CMS. Bestaande accounts hebben er nog wel
 * één in Firebase Auth (voor de webshop), maar hier wordt hij niet gebruikt.
 */
export const LoginForm = () => {
  const firebase = useFirebase();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  // De uitnodigingsmail linkt naar /auth/login?email=...; dan staat het adres
  // al ingevuld en hoeft de gebruiker alleen op "Stuur inlogcode" te klikken.
  const prefilledEmail = (searchParams.get("email") || "").trim().toLowerCase();

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(prefilledEmail);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const describeError = (error) => {
    switch (error?.code) {
      case "INVALID_CODE":
        return t("login_code.invalid_code");
      case "EXPIRED":
        return t("login_code.expired");
      case "TOO_MANY_ATTEMPTS":
        return t("login_code.too_many_attempts");
      case "TOO_MANY_REQUESTS":
        return t("login_code.too_many_requests");
      case "NETWORK_ERROR":
        return t("login_code.network_error");
      default:
        return t("login_code.request_failed");
    }
  };

  const sendCode = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      await requestLoginCode(email.trim());
      setStep("code");
      setCode("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      messageApi.success(t("login_code.code_sent", { email: email.trim() }), 5);
    } catch (error) {
      setErrorMessage(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== CODE_LENGTH) {
      setErrorMessage(t("login_code.enter_code"));
      return;
    }
    setErrorMessage("");
    setLoading(true);
    try {
      const { token } = await verifyLoginCode(email.trim(), digits);
      // react-redux-firebase: login met custom token, zodat ook de redux-auth
      // meteen klopt (PrivateRoute leest state.firebaseReducer.auth).
      await firebase.login({ token });
      navigate(dashboard);
    } catch (error) {
      setErrorMessage(describeError(error));
      setLoading(false);
    }
  };

  const backToEmail = () => {
    setStep("email");
    setCode("");
    setErrorMessage("");
  };

  return (
    <>
      {contextHolder}
      {step === "email" ? (
        <Form
          layout="vertical"
          name="login-email"
          onFinish={sendCode}
          className="mt-4"
          initialValues={{ email: prefilledEmail }}
        >
          <p className="text-center text-muted">{t("login_code.intro")}</p>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              { required: true, message: t("form.enter_email") },
              { type: "email", message: t("form.enter_valid_email") },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-primary" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
          </Form.Item>
          {errorMessage && (
            <div className="w-100 text-center mb-2">
              <span className="text-danger">{errorMessage}</span>
            </div>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {t("login_code.send_code")}
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <Form layout="vertical" name="login-code" onFinish={verify} className="mt-4">
          <p className="text-center text-muted">{t("login_code.code_sent", { email: email.trim() })}</p>
          <Form.Item label={t("login_code.code")} required>
            <Input
              prefix={<NumberOutlined className="text-primary" />}
              value={code}
              // Alleen cijfers: uit de mail geplakt staat er "108 116" (met een gewone of harde spatie)
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              size="large"
              style={{ letterSpacing: 4, textAlign: "center" }}
              autoFocus
            />
          </Form.Item>
          {errorMessage && (
            <div className="w-100 text-center mb-2">
              <span className="text-danger">{errorMessage}</span>
            </div>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {t("login_code.verify")}
            </Button>
          </Form.Item>
          <div className="d-flex flex-column justify-content-between align-items-center">
            <Button
              type="link"
              size="small"
              onClick={sendCode}
              disabled={cooldown > 0 || loading}
              style={{ padding: 0 }}
            >
              {cooldown > 0 ? t("login_code.resend_in", { seconds: cooldown }) : t("login_code.resend")}
            </Button>
          </div>
          <Text type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
            {t("login_code.help")}
          </Text>
        </Form>
      )}
    </>
  );
};

export default LoginForm;
